import type { MemoizedSelector } from '@ngrx/store';
import { createSelector } from '@ngrx/store';

import { memoizeSelector } from '../selector-utils';
import type { BaseMetaState, MetaState, Reference } from '../types';
import { createSelectorWithReferences } from '../utils';
import type {
  GenericEntity,
  BaseSelector,
  EntityState,
  ID,
  InferScopeMeta,
  GenericMetaOptions,
  MetaScopeOptions,
  Scope,
} from './types';
import { resolveScope } from './utils';

type MetaSelectorProps = ID | { scope: Scope };

// eslint-disable-next-line max-lines-per-function
export const getSelectors = <T extends GenericEntity, U extends GenericMetaOptions>(
  baseSelector: BaseSelector<T, U>,
  namespace: string,
  references: Array<Reference<T>> | null,
) => {
  const entityMap = createSelector(baseSelector, state => state.entityMap);
  const entityMetaMap = createSelector(baseSelector, state => state.entityMetaMap);
  const aliasesMap = createSelector(baseSelector, state => state.aliases);
  const scopeMapSelector = createSelector(baseSelector, state => state.scope);
  const scopePaginationMapSelector = createSelector(baseSelector, state => state.scopePagination);
  const scopeMetaMapSelector = createSelector(baseSelector, state => state.scopeMeta);
  const scopeMetaSelector = memoizeSelector((scope?: string | null) =>
    createSelector(scopeMetaMapSelector, scopeMetaMap => scopeMetaMap[resolveScope(scope)]),
  );
  const scopePaginationSelector = memoizeSelector((props?: MetaScopeOptions) => {
    const resolvedScope = resolveScope(props?.scope);
    return createSelector(
      scopePaginationMapSelector,
      scopePaginationMap => scopePaginationMap[resolvedScope],
    );
  });
  const idByAliasSelector = memoizeSelector((alias: string) =>
    createSelector(aliasesMap, aliasesMap => aliasesMap[alias]),
  );

  const entityMeta = memoizeSelector((id: ID) =>
    createSelector(entityMetaMap, entityMetaMap => entityMetaMap[id]),
  );

  const scopeSelector = memoizeSelector((scope?: string | null) =>
    createSelector(scopeMapSelector, scopeMap => {
      const resolvedScope = resolveScope(scope);
      return scopeMap[resolvedScope];
    }),
  );

  const byId = memoizeSelector((id: string | number) => {
    const idFromAlias = idByAliasSelector(`${id}`);
    return createSelector(
      entityMap,
      idFromAlias,
      (entities: EntityState<T>['entityMap'], alias: EntityState<T>['aliases'][number]) => {
        const entityId = alias || id;
        return entities[entityId] || null;
      },
    );
  });

  const getMetaSelector = <K extends keyof MetaState>(metaProp: K) =>
    memoizeSelector((scopeOrEntityId?: MetaSelectorProps) => {
      if (!scopeOrEntityId || (<{ scope: Scope }>scopeOrEntityId)?.scope) {
        const resolvedScope = resolveScope((<{ scope: Scope }>scopeOrEntityId)?.scope);
        return <MemoizedSelector<any, MetaState[K]>>(
          createSelector(scopeMetaSelector(resolvedScope), scopeMeta => scopeMeta?.[metaProp])
        );
      } else {
        const idOrAlias = <ID>scopeOrEntityId;
        const idFromAlias = idByAliasSelector(`${idOrAlias}`);
        return <MemoizedSelector<any, MetaState[K]>>createSelector(
          entityMeta(idOrAlias),
          state => entityMeta(idFromAlias(state))(state),
          (metaFromId, metaFromAlias) => (metaFromId || metaFromAlias)?.[metaProp],
        );
      }
    });

  const isLoaded = getMetaSelector('loaded');
  const isPending = getMetaSelector('pending');
  const isFailed = getMetaSelector('failed');
  const error = getMetaSelector('error');
  const expiresAt = getMetaSelector('expiresAt');
  const scopeCustomMeta = memoizeSelector((options?: { scope: Scope }) =>
    createSelector(baseSelector, (state: EntityState<T, U>) => {
      const scope = resolveScope(options?.scope);
      const scopeMeta = <BaseMetaState & { custom: InferScopeMeta<U> }>state.scopeMeta[scope];
      return scopeMeta?.custom;
    }),
  );

  const meta = memoizeSelector((idOrScope?: MetaSelectorProps) =>
    createSelector(
      isLoaded(idOrScope),
      isPending(idOrScope),
      isFailed(idOrScope),
      error(idOrScope),
      expiresAt(idOrScope),
      (isLoaded: boolean, isPending: boolean, isFailed: boolean, error: any, expiresAt: any) => ({
        isLoaded,
        isPending,
        isFailed,
        error,
        expiresAt,
      }),
    ),
  );

  const entities = memoizeSelector((props?: MetaScopeOptions) =>
    createSelector(
      scopeSelector(props?.scope),
      entityMap,
      (scope: EntityState<T>['scope'][number], entityMap: EntityState<T>['entityMap']) =>
        scope?.map(id => entityMap[id]!) || [],
    ),
  );

  const allEntities = createSelector(
    entityMap,
    entityMap => <T[]>Object.values(entityMap).filter(Boolean),
  );

  return {
    aliasesMap,
    entityMap,
    entityMetaMap,
    isLoaded,
    isPending,
    isFailed,
    error,
    meta,
    pagination: scopePaginationSelector,
    byId,
    scopeCustomMeta,
    byIdWithReferences: memoizeSelector((id: number | string) =>
      createSelectorWithReferences(byId(id), references, state => state),
    ),
    entities,
    allEntities,
    allEntitiesWithReferences: createSelectorWithReferences(
      allEntities,
      references,
      entities => entities,
    ),
    entitiesWithPagination: memoizeSelector((props?: MetaScopeOptions) =>
      createSelector(scopePaginationSelector(props), entities(props), (pagination, entities) => ({
        meta: pagination,
        rows: entities,
      })),
    ),
    entitiesWithReferences: memoizeSelector((props?: MetaScopeOptions) =>
      createSelectorWithReferences(entities(props), references, entities => entities),
    ),
  };
};
